import { Box, GridLegacy as Grid, Grow, Typography, useTheme } from '@mui/material';

import { FoodMenuModel, useGetFoodMenus } from '@/logic/services/food-menu';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';

import { AxiosError } from 'axios';
import React from 'react';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';

import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import { useGetSettings } from '@/logic/services';
import FoodMenuCard from '@/components/food-menu/food-menu-card';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const foodEmptyCard = '/icons/foodMenu/food-empty-card.svg';

export const FoodMenu: NextPage<{
  path: string;
}> = ({ path }) => {
  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.foodMenu);

  const { data: pageData, isLoading } = useGetFoodMenus();
  const theme = useTheme();
  const intl = useIntl();

  const { data: allCmsSetting } = useGetSettings();

  return (
    <>
      <Seo
        title={seoPage?.titleTag || intl.formatMessage({ id: 'food_menu' })}
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag || allCmsSetting?.experienceDescription}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      <Box
        component='section'
        sx={{
          flexGrow: 1,
          px: {
            laptop: pxToRem(128),
            tablet: pxToRem(60),
            mobile: '16px',
          },
          mb: pxToRem(122),
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: pxToRem(30),
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='food_menu' />
        </Typography>
        {!isLoading ? (
          pageData?.data?.length > 0 ? (
            <Box>
              {allCmsSetting?.foodMenuDescription && (
                <Typography
                  component='p'
                  sx={{
                    fontSize: pxToRem(24),
                    color: theme.palette.grey[50],
                    textAlign: 'center',
                    mb: { tablet: pxToRem(44), mobile: '24px' },
                    maxWidth: '1094px',
                    mx: 'auto',
                    lineHeight: pxToRem(36),
                  }}
                >
                  {allCmsSetting.foodMenuDescription}
                </Typography>
              )}
              <Grid container columnSpacing={5}>
                {pageData?.data.map((foodMenu: FoodMenuModel, index: number) => (
                  <Grow
                    in
                    style={{
                      transformOrigin: '0 0 0',
                    }}
                    {...{ timeout: 1000 + index * 500 }}
                    key={foodMenu.id}
                  >
                    <Grid item tablet={6} mobile={12} laptop={4}>
                      <FoodMenuCard
                        image={foodMenu?.thumbnailUrl || foodEmptyCard}
                        id={foodMenu.id}
                        pdfLink={foodMenu?.fileUrl}
                      />
                    </Grid>
                  </Grow>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box
              component='div'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                pt: { tablet: pxToRem(37), mobile: '26px' },
              }}
            >
              <ImageBoxFuture
                img='/icons/foodMenu/food-menu-empty-state.svg'
                title={intl.formatMessage({
                  id: 'alt.food_empty_icon',
                })}
                objectFit='contain'
                sx={{
                  mb: { tablet: pxToRem(24), mobile: '24px' },
                  width: { tablet: pxToRem(232), mobile: '216px' },
                  height: { tablet: pxToRem(182), mobile: '145px' },
                }}
              />

              <Typography
                component='p'
                sx={{
                  fontSize: { tablet: pxToRem(20), mobile: '16px' },
                  textAlign: 'center',
                  color: theme.palette.grey[50],
                  maxWidth: { tablet: pxToRem(461), mobile: '461px' },
                  lineHeight: '150%',
                }}
              >
                <FormattedMessage id='food_menu.no_menu' />
              </Typography>
            </Box>
          )
        ) : (
          <MovieLoaderWrapper
            innerBox={{
              width: '160px',
              height: '160px',
              [theme.breakpoints.down('laptop')]: {
                width: '100px',
                height: '100px',
              },
            }}
            outerBox={{
              width: '100%',
              height: {
                mobile: 'calc(100vh - 56px)',
                tablet: 'calc(100vh - 100px)',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const queryClient = new QueryClient();

  try {
    await useGetFoodMenus.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};
export default FoodMenu;
